import {
    useEffect,
    useRef,
    type FC
} from "react";
import {
    type TextStyle,
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import type IPaginationControlProps from "./type";
import type {
    PaginationControlNativePropsType,
    PaginationControlWebPropsType
} from "./type";
import stylesheet, {
    PAGINATION_SELECTED_VARIANTS,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePressScale
} from "../../../../helpers/motion";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    getToggleColors,
    getToggleSize
} from "../../../toggle/stylesheet";
import {
    splitControlStyle
} from "../../../../helpers/theme/control";
import {
    getIsPaginationAttributeNode
} from "../../util";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const PaginationControl: FC<IPaginationControlProps> = ({
    "aria-label": ariaLabel,
    iconDirection = "start",
    isAriaDisabled = false,
    icon: IconComponent,
    isDisabled = false,
    isIconOnly = false,
    isCurrent = false,
    customTheme,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    onFocus,
    onPress,
    onBlur,
    style,
    title,
    href,
    kind,
    size,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const controlRef = useRef<View>(null);

    const isVisualDisabled = isDisabled || isAriaDisabled;

    const pressScaleStyle = usePressScale({
        isDisabled: isVisualDisabled || isCurrent,
        customTheme,
        isPressed
    });

    const isHighlighted = !isVisualDisabled && !isCurrent && (isHovered || isPressed);

    const isIconOnlyControl = isIconOnly && !!IconComponent;

    const currentSize = getToggleSize({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const currentColors = getToggleColors({
        isDisabled: isVisualDisabled,
        isActive: isCurrent,
        variant: "ghost",
        isHighlighted,
        colors
    });

    const {
        container: containerDynamicStyle,
        content: contentDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        borderRadius: radiuses.actions,
        isIconOnly: isIconOnlyControl,
        isFocusVisible,
        currentColors,
        shapeConfigs,
        touchTargets,
        currentSize,
        pointerType,
        borders,
        colors,
        spaces,
        kind
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    useEffect(() => {
        const controlNode: unknown = controlRef.current;

        if(Platform.OS !== "web" || isDisabled || !getIsPaginationAttributeNode(controlNode)) {
            return;
        }

        if(isAriaDisabled) {
            controlNode.setAttribute("aria-disabled", "true");

            return;
        }

        controlNode.removeAttribute("aria-disabled");
    }, [
        isAriaDisabled,
        isDisabled
    ]);

    const handlePress = () => {
        if(isVisualDisabled || isCurrent) {
            return;
        }

        onPress?.();
    };

    const renderIcon = (direction: "start" | "end") => {
        if(!IconComponent || iconDirection !== direction) {
            return null;
        }

        return <IconComponent
            customColor={currentColors.iconColor}
            color={currentColors.iconColorKey}
            size={currentSize.iconSize}
        />;
    };

    const renderTitle = () => {
        if(!title || isIconOnlyControl) {
            return null;
        }

        return <Text
            variant={isCurrent && !isVisualDisabled ? PAGINATION_SELECTED_VARIANTS[size] : currentSize.labelVariant}
            style={[
                stylesheet.title,
                isCurrent && !isVisualDisabled && size === "small" ? {
                    fontWeight: typography.titleSmallSize.fontWeight as TextStyle["fontWeight"]
                } : undefined
            ]}
            customColor={currentColors.titleColor}
            customTheme={customTheme}
            numberOfLines={1}
        >
            {title}
        </Text>;
    };

    const webProps: PaginationControlWebPropsType = Platform.OS === "web" ? {
        ...isCurrent ? {
            "aria-current": "page"
        } : {},
        ...href && !isVisualDisabled ? {
            href
        } : {}
    } : {};

    const nativeProps: PaginationControlNativePropsType = Platform.OS === "web" ? {} : {
        accessibilityState: {
            disabled: isVisualDisabled,
            selected: isCurrent
        }
    };

    return <AnimatedPressable
        {...props}
        {...webProps}
        {...nativeProps}
        {...interactionHandlers}
        role={webProps.href ? "link" : "button"}
        style={[
            containerStyle,
            stylesheet.container,
            containerDynamicStyle,
            pressScaleStyle
        ]}
        aria-label={ariaLabel}
        onPress={handlePress}
        disabled={isDisabled}
        ref={controlRef}
    >
        <View
            style={[
                surfaceStyle,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                isHovered={isHighlighted && isHovered}
                isPressed={isHighlighted && isPressed}
                borderRadius={radiuses.actions}
                borderWidth={borders.line}
                customTheme={customTheme}
            />
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {renderIcon("start")}
                {renderTitle()}
                {renderIcon("end")}
            </View>
        </View>
    </AnimatedPressable>;
};
export default PaginationControl;
